import type { ComponentType } from 'react';
import React, { useContext } from 'react';

import { RicosContext } from './../../../../ricos-context';
import { GalleryOptions_Layout_Type } from './../../../../ricos-schema/next-schema';
import type { LogBiFn } from './../../../../_shared/bi-context-provider/bi-context-provider';
import { useNodeAttrs } from './../../../../_shared/use-node-attrs';
import { ThumbnailPicker, usePanelContainerElement } from './../../../../ui-components';

import type { SupportedLayouts } from '../../../common-types';
import {
  LAYOUT_SETTINGS_LAYOUT_THUMBNAIL,
  LAYOUT_SETTINGS_LAYOUT_THUMBNAIL_PICKER,
} from '../../../data-hooks';
import {
  LayoutCollageIcon,
  LayoutColumnsIcon,
  LayoutGridIcon,
  LayoutMasonryIcon,
  LayoutPanoramaIcon,
  LayoutSlideshowIcon,
  LayoutSlidesIcon,
  LayoutThumbnailsIcon,
} from '../../../icons';
import { layoutRicosData } from '../../../layout-data-provider';
import { LayoutControlsSection } from './layout-controls-section/layout-controls-section';

type LayoutSettingsProps = {
  nodeId: string;
  logBi: LogBiFn;
};

export const LayoutSettings: React.FC<LayoutSettingsProps> = ({ nodeId, logBi }) => {
  const { t } = useContext(RicosContext);
  const containerEl = usePanelContainerElement();
  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, 'GALLERY');

  const layoutType = pmNodeAttrs?.options?.layout?.type;

  return pmNodeAttrs && pmNodeAttrs.options && layoutType ? (
    <>
      <ThumbnailPicker
        selectedValue={layoutType}
        onChange={galleryLayout => {
          const defaultData = {
            ...pmNodeAttrs.options,
            ...layoutRicosData[galleryLayout as SupportedLayouts],
          };
          patchPmNodeAttrs({
            ...pmNodeAttrs,
            options: {
              ...defaultData,
              layout: { ...defaultData?.layout, type: galleryLayout as SupportedLayouts },
            },
          });
        }}
        dataSource={galleryLayouts.map(({ label, value, icon: Icon }) => ({
          value,
          dataHook: `${LAYOUT_SETTINGS_LAYOUT_THUMBNAIL}${value}`,
          renderBackgroundImage: props => <Icon {...props} />,
          label: t(label),
        }))}
        tooltipAppendTo={containerEl}
        dataHook={LAYOUT_SETTINGS_LAYOUT_THUMBNAIL_PICKER}
        logBi={logBi}
      />
      <LayoutControlsSection
        layout={layoutType}
        data={pmNodeAttrs}
        updateData={patchPmNodeAttrs}
        logBi={logBi}
      />
    </>
  ) : null;
};

const galleryLayouts: {
  value: GalleryOptions_Layout_Type;
  label: string;
  icon: ComponentType<{ width?: string; height?: string; margin?: string }>;
}[] = [
  {
    value: GalleryOptions_Layout_Type.GRID,
    label: 'GalleryPlugin_Layout_Grid',
    icon: LayoutGridIcon,
  },
  {
    value: GalleryOptions_Layout_Type.MASONRY,
    label: 'GalleryPlugin_Layout_Masonry',
    icon: LayoutMasonryIcon,
  },
  {
    value: GalleryOptions_Layout_Type.COLLAGE,
    label: 'GalleryPlugin_Layout_Collage',
    icon: LayoutCollageIcon,
  },
  {
    value: GalleryOptions_Layout_Type.THUMBNAIL,
    label: 'GalleryPlugin_Layout_Thumbnails',
    icon: LayoutThumbnailsIcon,
  },
  {
    value: GalleryOptions_Layout_Type.FULLSIZE,
    label: 'GalleryPlugin_Layout_Slideshow',
    icon: LayoutSlideshowIcon,
  },
  {
    value: GalleryOptions_Layout_Type.PANORAMA,
    label: 'GalleryPlugin_Layout_Panorama',
    icon: LayoutPanoramaIcon,
  },
  {
    value: GalleryOptions_Layout_Type.COLUMN,
    label: 'GalleryPlugin_Layout_Columns',
    icon: LayoutColumnsIcon,
  },
  {
    value: GalleryOptions_Layout_Type.SLIDER,
    label: 'GalleryPlugin_Layout_Slides',
    icon: LayoutSlidesIcon,
  },
];
